import Intercom from '@intercom/messenger-js-sdk';
import { useState, useEffect, useContext, createContext } from 'react';

import { api, endpoints } from 'src/lib/api';
import { useOrganisation } from 'src/organisation';
import { logErrorToDatadog } from 'src/observability/datadog-helpers';

// ----------------------------------------------------------------------

type IntercomRegion = 'us' | 'eu' | 'ap';

const MessengerReadyContext = createContext(false);

export const useIsIntercomMessengerReady = () => useContext(MessengerReadyContext);

/**
 * Boots the Intercom Messenger as the signed-in recruiter once the backend has signed their identity.
 * Environments without an Intercom workspace never boot it, so useIsIntercomMessengerReady stays false
 * and no launcher renders.
 */
export function IntercomMessengerProvider({ children }: { children: React.ReactNode }) {
  const { id } = useOrganisation();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!id) return undefined;

    let isCurrent = true;

    api(endpoints.intercomMessenger.get)
      .then((session) => {
        if (!isCurrent || !session) return;

        Intercom({
          app_id: session.AppId,
          region: session.Region as IntercomRegion,
          intercom_user_jwt: session.UserJwt,
          hide_default_launcher: true,
          alignment: 'left',
        });
        setIsReady(true);
      })
      .catch(logErrorToDatadog);

    return () => {
      isCurrent = false;
    };
  }, [id]);

  return (
    <MessengerReadyContext.Provider value={isReady}>{children}</MessengerReadyContext.Provider>
  );
}
